import React, { useMemo, useState } from 'react';
import { useGetRequest, usePutRequest } from '@/utils/useStatefulRequests';
import { ListResponse } from '@/wpt-lib/interfaces/listResponse';
import { Faculty } from '@/wpt-lib/interfaces/Faculty';
import { Button, FormLabel, HStack, Switch, Text, VStack } from '@chakra-ui/react';
import StateDisplay from '@/components/StateDisplay/StateDisplay';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';

import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import { useAsyncEffect } from '@/utils/hooks/useAsyncEffect';
import { isNullish } from '@/wpt-lib/utils/isNullish';

import FacultyEditorTable from '@/components/pages/Admin/Faculty/FacultyEditorTable';

interface UpdateFacultyDto {
    updates: {
        id: number;
        label?: string;
        inactive?: boolean;
    }[];
}

const FacultyEditor = () => {
    const { currentAssignment } = useAssignment();
    const { error, loading, responseData, makeRequest } = useGetRequest<ListResponse<Faculty>>();
    const { error: saveError, loading: saveLoading, makeRequest: save } = usePutRequest<Faculty[], UpdateFacultyDto>();

    const [updated, setUpdated] = useState<Faculty[]>([]);
    const [showInactive, setShowInactive] = useState(false);

    const loadFaculties = async () => {
        try {
            await makeRequest(`faculty`, {
                params: {
                    all: true,
                },
            });
        } catch (e) {
            // handled by hook
        }
    };

    useAsyncEffect(loadFaculties, []);

    const onUpdateFaculty = (faculty: Faculty) => {
        setUpdated([...(updated || []).filter((f) => f.id !== faculty.id), faculty]);
    };

    const handleSave = async () => {
        if (updated.length === 0) return;

        await save('faculty', { updates: updated }, { params: { assignment: currentAssignment?.id } });
        setUpdated([]);
        await loadFaculties();
    };

    const faculties = useMemo(() => {
        const list =
            responseData?.items?.map((faculty) => ({
                ...faculty,
                ...(updated.find((updatedFaculty) => updatedFaculty.id === faculty.id) ?? {}),
                changed: updated.some((updatedFaculty) => updatedFaculty.id === faculty.id),
            })) ?? [];

        list.sort((a, b) => a.name.localeCompare(b.name));
        return showInactive ? list : list.filter((f) => !f.inactive || f.changed);
    }, [responseData, updated, showInactive]);

    return (
        <VStack alignItems={'flex-start'} spacing={3} w="full">
            <Text>This section allows you to mark faculties as active or inactive.</Text>
            <StateDisplay
                data={faculties}
                loading={loading}
                error={!!error}
                errorRender={() => <SimpleAlert variant="subtle">Could not load faculty list</SimpleAlert>}
                render={() => {
                    return (
                        <VStack w="100%" alignItems={'flex-end'}>
                            <HStack mt={-8}>
                                <FormLabel htmlFor="show-inactive-faculties" mb={0}>
                                    Show all faculties
                                </FormLabel>
                                <Switch
                                    id="show-inactive-faculties"
                                    isChecked={showInactive}
                                    onChange={() => setShowInactive((v) => !v)}
                                    colorScheme="blue"
                                />
                            </HStack>
                            <FacultyEditorTable faculties={faculties} onUpdate={onUpdateFaculty} />
                            <HStack>
                                {updated.length > 0 && (
                                    <Text>
                                        {updated.length} facult{updated.length === 1 ? 'y' : 'ies'} with pending changes
                                    </Text>
                                )}
                                {!isNullish(saveError) && (
                                    <Text>Error saving changes, please refresh page and try again.</Text>
                                )}
                                <Button isDisabled={updated.length === 0 || saveLoading} onClick={() => setUpdated([])}>
                                    Reset
                                </Button>
                                <Button
                                    isDisabled={updated.length === 0}
                                    isLoading={saveLoading}
                                    onClick={handleSave}
                                    colorScheme="blue"
                                >
                                    Save
                                </Button>
                            </HStack>
                        </VStack>
                    );
                }}
            />
        </VStack>
    );
};

export default FacultyEditor;
